import React, { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const EMPTY_PRODUCT = { name: '', manufacturer: '', model: '', category_id: '' };

function Modal({ title, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999, padding: 24 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="card" style={{ width: '100%', maxWidth: 460, padding: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22 }}>
          <h2 style={{ fontSize: 17, fontWeight: 700 }}>{title}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--text-secondary)', lineHeight: 1, cursor: 'pointer' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function Products() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]); // used for counts only
  const [selectedCat, setSelectedCat] = useState(null);

  // Category modal
  const [catModal, setCatModal] = useState(null);
  const [catName, setCatName] = useState('');
  const [catError, setCatError] = useState('');
  const [catSaving, setCatSaving] = useState(false);

  // Product modal
  const [prodModal, setProdModal] = useState(null);
  const [prodForm, setProdForm] = useState(EMPTY_PRODUCT);
  const [prodError, setProdError] = useState('');
  const [prodSaving, setProdSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadCategories = useCallback(async () => {
    const data = await apiFetch('/products/categories');
    setCategories(data);
  }, []);

  const loadAllProducts = useCallback(async () => {
    const data = await apiFetch('/products');
    setAllProducts(data);
  }, []);

  const loadProducts = useCallback(async () => {
    if (!selectedCat) { setProducts([]); return; }
    const data = await apiFetch(`/products?category_id=${selectedCat.id}`);
    setProducts(data);
  }, [selectedCat]);

  useEffect(() => { loadCategories(); loadAllProducts(); }, [loadCategories, loadAllProducts]);
  useEffect(() => { loadProducts(); }, [loadProducts]);

  // ── Category actions ──
  function openAddCat() { setCatName(''); setCatError(''); setCatModal('add'); }
  function openEditCat(e, c) { e.stopPropagation(); setCatName(c.name); setCatError(''); setCatModal(c); }

  async function saveCat(e) {
    e.preventDefault();
    setCatError(''); setCatSaving(true);
    try {
      if (catModal === 'add') {
        await apiFetch('/products/categories', { method: 'POST', body: { name: catName } });
      } else {
        await apiFetch(`/products/categories/${catModal.id}`, { method: 'PATCH', body: { name: catName } });
        if (selectedCat?.id === catModal.id) setSelectedCat(c => ({ ...c, name: catName }));
      }
      setCatModal(null);
      loadCategories();
    } catch (err) {
      setCatError(err.message);
    } finally {
      setCatSaving(false);
    }
  }

  // ── Product actions ──
  function openAddProd() {
    setProdForm({ ...EMPTY_PRODUCT, category_id: selectedCat?.id || '' });
    setProdError('');
    setProdModal('add');
  }

  function openEditProd(p) {
    setProdForm({ name: p.name, manufacturer: p.manufacturer || '', model: p.model || '', category_id: p.category_id || '' });
    setProdError('');
    setProdModal({ ...p });
  }

  async function saveProd(e) {
    e.preventDefault();
    setProdError(''); setProdSaving(true);
    try {
      if (prodModal === 'add') {
        await apiFetch('/products', { method: 'POST', body: prodForm });
      } else {
        await apiFetch(`/products/${prodModal.id}`, { method: 'PATCH', body: prodForm });
      }
      setProdModal(null);
      loadProducts();
      loadAllProducts();
    } catch (err) {
      setProdError(err.message);
    } finally {
      setProdSaving(false);
    }
  }

  async function confirmDelete() {
    const { type, item } = deleteTarget;
    if (type === 'cat') {
      await apiFetch(`/products/categories/${item.id}`, { method: 'DELETE' });
      if (selectedCat?.id === item.id) setSelectedCat(null);
      loadCategories();
      loadAllProducts();
      setProducts([]);
    } else {
      await apiFetch(`/products/${item.id}`, { method: 'DELETE' });
      loadProducts();
      loadAllProducts();
    }
    setDeleteTarget(null);
  }

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '32px 24px' }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 6 }}>Products</h1>
      <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 28 }}>First create a category, then add products within it.</p>

      {/* ── STEP 1: Categories ── */}
      <div className="card" style={{ padding: 24, marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ background: '#0B6623', color: '#fff', borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700 }}>1</span>
            <h2 style={{ fontSize: 16, fontWeight: 600 }}>Select or Create a Category</h2>
          </div>
          <button className="btn btn-primary btn-sm" onClick={openAddCat}>+ Add Category</button>
        </div>

        {categories.length === 0 ? (
          <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--text-secondary)', fontSize: 14 }}>
            No categories yet — click <strong>+ Add Category</strong> to get started.
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {categories.map(c => {
              const active = selectedCat?.id === c.id;
              return (
                <div key={c.id}
                  onClick={() => setSelectedCat(active ? null : c)}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 999, border: `2px solid ${active ? 'var(--status-resolved-text)' : 'var(--border-subtle)'}`, background: active ? 'var(--action-bg)' : 'var(--bg-surface)', cursor: 'pointer', fontWeight: active ? 600 : 400, fontSize: 14, color: active ? '#0B6623' : 'var(--text-muted)', transition: 'all .15s' }}>
                  <span>{c.name}</span>
                  <span style={{ fontSize: 12, color: active ? '#0d7a28' : 'var(--text-secondary)' }}>
                    ({allProducts.filter(p => p.category_id === c.id).length})
                  </span>
                  <button onClick={e => openEditCat(e, c)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: active ? '#0d7a28' : 'var(--text-secondary)', fontSize: 13, padding: '0 2px', lineHeight: 1 }}>✏</button>
                  {isSuperAdmin && (
                    <button onClick={e => { e.stopPropagation(); setDeleteTarget({ type: 'cat', item: c }); }} title="Delete category (super admin only)" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#fca5a5', fontSize: 13, padding: '0 2px', lineHeight: 1 }}>✕</button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── STEP 2: Products ── */}
      <div className="card" style={{ padding: 24, opacity: selectedCat ? 1 : 0.5, transition: 'opacity .2s' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ background: selectedCat ? '#0B6623' : '#d1d5db', color: '#fff', borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700 }}>2</span>
            <h2 style={{ fontSize: 16, fontWeight: 600 }}>
              {selectedCat ? `Products in "${selectedCat.name}"` : 'Select a category above to manage its products'}
            </h2>
          </div>
          {selectedCat && (
            <button className="btn btn-primary btn-sm" onClick={openAddProd}>+ Add Product</button>
          )}
        </div>

        {!selectedCat ? (
          <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--text-secondary)', fontSize: 14 }}>
            Click a category above to view and add products.
          </div>
        ) : products.length === 0 ? (
          <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-secondary)', fontSize: 14 }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>📦</div>
            No products in this category yet.
            <br />
            <button className="btn btn-primary btn-sm" style={{ marginTop: 12 }} onClick={openAddProd}>+ Add First Product</button>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: 'var(--bg-app)', borderBottom: '1px solid var(--border-subtle)' }}>
                {['Product Name', 'Manufacturer', 'Model', ''].map(h => (
                  <th key={h} style={{ padding: '9px 14px', textAlign: 'left', color: 'var(--text-secondary)', fontWeight: 500, fontSize: 12 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {products.map(p => (
                <tr key={p.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  <td style={{ padding: '11px 14px', fontWeight: 500 }}>{p.name}</td>
                  <td style={{ padding: '11px 14px', color: 'var(--text-secondary)' }}>{p.manufacturer || '—'}</td>
                  <td style={{ padding: '11px 14px', color: 'var(--text-secondary)' }}>{p.model || '—'}</td>
                  <td style={{ padding: '11px 14px', whiteSpace: 'nowrap' }}>
                    <button className="btn btn-secondary btn-sm" style={{ marginRight: 6 }} onClick={() => openEditProd(p)}>Edit</button>
                    {isSuperAdmin && (
                      <button className="btn btn-sm" style={{ background: 'var(--red-50)', color: '#b91c1c' }} onClick={() => setDeleteTarget({ type: 'prod', item: p })}>Delete</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Category modal */}
      {catModal !== null && (
        <Modal title={catModal === 'add' ? 'Add Category' : 'Edit Category'} onClose={() => setCatModal(null)}>
          {catError && <div style={{ background: 'var(--red-50)', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 14 }}>{catError}</div>}
          <form onSubmit={saveCat}>
            <div className="form-group">
              <label className="label">Category Name *</label>
              <input className="input" value={catName} onChange={e => setCatName(e.target.value)} required autoFocus />
            </div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
              <button type="button" className="btn btn-secondary" onClick={() => setCatModal(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={catSaving}>{catSaving ? 'Saving…' : (catModal === 'add' ? 'Add Category' : 'Save')}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Product modal */}
      {prodModal !== null && (
        <Modal title={prodModal === 'add' ? 'Add Product' : 'Edit Product'} onClose={() => setProdModal(null)}>
          {prodError && <div style={{ background: 'var(--red-50)', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 14 }}>{prodError}</div>}
          <form onSubmit={saveProd}>
            <div className="form-group">
              <label className="label">Product Name *</label>
              <input className="input" value={prodForm.name} onChange={e => setProdForm(f => ({ ...f, name: e.target.value }))} required autoFocus />
            </div>
            <div className="form-group">
              <label className="label">Manufacturer</label>
              <input className="input" value={prodForm.manufacturer} onChange={e => setProdForm(f => ({ ...f, manufacturer: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="label">Model</label>
              <input className="input" value={prodForm.model} onChange={e => setProdForm(f => ({ ...f, model: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
              <button type="button" className="btn btn-secondary" onClick={() => setProdModal(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={prodSaving}>{prodSaving ? 'Saving…' : (prodModal === 'add' ? 'Add Product' : 'Save Changes')}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <Modal title={`Delete ${deleteTarget.type === 'cat' ? 'Category' : 'Product'}?`} onClose={() => setDeleteTarget(null)}>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 24 }}>
            {deleteTarget.type === 'cat'
              ? `Deleting "${deleteTarget.item.name}" will unassign its products but not delete them.`
              : `This will permanently delete "${deleteTarget.item.name}".`}
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary" onClick={() => setDeleteTarget(null)}>Cancel</button>
            <button className="btn btn-danger" onClick={confirmDelete}>Delete</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
